import { Box, Typography } from "@mui/material";
import moment from "moment";
import { atStation, zoneOfLocation } from "../../util/stationTime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getI18n, useTranslation } from "react-i18next";
import { MdKeyboardArrowDown } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  bookingsApiSlice,
  useLazyGetUserOrderQuery,
} from "../../api/bookings/bookingsApiSlice";
import { locationsApiSlice } from "../../api/locations/locationsApiSlice";
import {
  setBookingSummary,
  setIsFirstTimeUsingSelectedLocker,
  setIsPaymentVerified,
  setIsSelectedBookingHostKeyRetrieval,
  setRentalStarted,
  setRentalFinished,
  resetRentalFinished,
  setSelectedBooking,
} from "../../redux/slice/booking/bookingSlice";
import {
  checkIfOrderCanBeStarted,
  formatCurrency,
  getBookingStatusType,
  hasOpenBalance,
  isEmptyObject,
  isVenueReviewPending,
} from "../../util";
import {
  setHasSelectedLocation,
  setSelectedLocation,
} from "../../redux/slice/bookingCreation/bookingCreationSlice";
import { enqueueNotification } from "../../redux/slice/notification/notificationSlice";
import {
  useCancelMyBookingMutation,
  useGetMyBookingsQuery,
} from "../../api/account/accountApiSlice";
import { deriveSelfCancelState, refusalTextKey, SelfCancelState } from "./selfCancelState";
import {
  classifyOrderLoadFailure,
  failureIsRetryable,
  failureMessageKey,
  OrderLoadFailure,
} from "./orderLoadFailure";
// ShadCN + Tailwind, per this workspace's CLAUDE.md: new code does not reach
// for MUI or the `sx` prop, however much of the surrounding file still does.
import { Button } from "components/ui/button";
import { baseStore, RootState } from "../../redux/store";
import routes from "../../routes/routes";
import KolulaStorageManager from "../../storage/KolulaStorageManager";
import {
  BookingSummary as BookingSummaryProps,
  Item,
} from "../../types/bookingCreation";
import { Reservation, ServiceTypeSelectorOption } from "../../types/order";
import { transformReservations } from "../../util";
import { hasOvertimeAllowance } from "../../util/overtime";
import { pruneLocationVerifications } from "../../util/locationVerification";
import BookingSummary, {
  BookingGroup,
} from "../bookLocation/common/components/BookingSummary/BookingSummary";
import SubscriptionSummary from "../bookLocation/common/components/SubscriptionSummary/SubscriptionSummary";
import BookingSummaryNote from "../bookLocation/common/components/BookingSummary/BookingSummaryNote";
import CustomButton from "../layout/components/CustomButton";
import Header from "../layout/components/Header";
import LoaderOverlay from "../layout/components/LoaderOverlay";
import TenantLogo from "../layout/components/TenantLogo";
import SelectLocationDialog from "../locationSelection/SelectLocationDialog";
import ServiceTypeSelector from "./components/ServiceTypeSelector";
import BookLocationHeader from "../bookLocation/common/util/BookLocationHeader";
import { useAccountOrderIdSource } from "./hooks/useAccountOrderIdSource";
import { i18n } from "../../i18n/i18n";
import { useStartRental } from "../../redux/flows/startRentalFlow";
import { useFinishRental } from "../../redux/flows/finishRentalFlow";
import { useGetUsageLogQuery } from "../../api/usageLog/usageLogApiSlice";
import { deriveUsageStatus } from "../useOrder/generic/useUsageLifecycle";
import { useLazyGetCustomerAgreementsByIdsQuery, useCancelSubscriptionMutation } from "api/customerAgreements/customerAgreementsApiSlice";
import { withdrawErrorMessage } from "../bookLocation/keyHandover/common/subscriptionManage";
import { getSubscriptionActions } from "../../util/subscriptionActions";
import GeneralDialog from "modules/common/GeneralDialog";
import StopUsingDialog from "../useOrder/steps/StopUsingDialog";
import { useUseCaseFlow } from "../useOrder/generic/useUseCaseFlow";
import { isRoomFamily, spaceOf } from "../../util/useCases";
import { supportsStartRental } from "./startRental";

const SUBSCRIPTIONS = "subscriptions";

const styles = {
  outerBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  innerBox: {
    maxWidth: 1000,
    margin: 0,
    padding: "0 20px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  logo: {
    width: "200px",
    marginTop: "40px",
    marginBottom: "40px",
    // Use the theme's breakpoints
    "@media (min-width:1200px)": {
      width: "300px",
    },
  },
  heading: {
    lineHeight: "28px",
    letterSpacing: "2px",
    fontWeight: "600",
  },
  buttonWrapper: {
    height: "100%",
    width: "100%",
    border: "1px solid red",
  },
};

/**
 * A failed self-cancel, in the app's own words: the server's refusal code
 * mapped to its `refusal_*` text, never the server's English sentence; no
 * code (a network failure) is the generic retry message.
 */
const cancelErrorMessage = (
  t: (key: string, options?: Record<string, unknown>) => string,
  error: { data?: { code?: unknown } } | undefined
): string =>
  typeof error?.data?.code === "string"
    ? t(refusalTextKey(error.data.code))
    : t("orders.cancel_booking.error_message");

/** The confirm dialog's text: the question, then what cancelling costs (M10). */
const cancelDialogContent = (
  t: (key: string, options?: Record<string, unknown>) => string,
  state: SelfCancelState,
  timeZone: string,
  language: string
): string => {
  const question = t("orders.cancel_booking.dialog_content", {
    bookingNumber: state.bookingNumber,
  });
  const terms = state.terms;
  if (!terms) return question;
  if (terms.kind === "agb") return `${question} ${t("orders.cancel_booking.terms_agb")}`;
  if (terms.kind === "withdraw") return `${question} ${t("orders.cancel_booking.terms_withdraw")}`;
  if (terms.kind === "free") return `${question} ${t("orders.cancel_booking.terms_free")}`;
  if (terms.kind === "fee") {
    return `${question} ${t("orders.cancel_booking.terms_fee", {
      percent: terms.percent,
      amount: formatCurrency(terms.amount, language),
    })}`;
  }
  const date = atStation(terms.freeUntil, timeZone).format(
    t("book_location.booking_summary.cancellation.date_format")
  );
  const termsKey =
    terms.kind === "covered"
      ? "orders.cancel_booking.terms_covered"
      : "orders.cancel_booking.terms_free_until";
  return `${question} ${t(termsKey, { date })}`;
};

const SelfCancelSection = ({
  state,
  busy,
  onCancelClick,
  contact,
}: {
  state: SelfCancelState;
  busy: boolean;
  onCancelClick: () => void;
  /** The operator's e-mail / phone, for a booking that cannot be cancelled here. */
  contact: string;
}) => {
  const { t } = useTranslation();
  if (state.canceledNote) {
    return (
      <p className="my-2 text-sm text-foreground/60 text-center">
        {t("orders.cancel_booking.canceled_note")}
        {state.canceledFee !== null && (
          <>
            {" "}
            {state.canceledFee > 0
              ? t("orders.cancel_booking.canceled_fee", {
                  amount: formatCurrency(state.canceledFee, i18n.language),
                })
              : t("orders.cancel_booking.canceled_free")}
          </>
        )}
      </p>
    );
  }
  if (state.refusal) {
    // Why not, and who can — never a silently missing button (M10).
    return (
      <p className="my-2 text-sm text-foreground/60 text-center">
        {t(refusalTextKey(state.refusal))}{" "}
        {contact
          ? t("orders.cancel_booking.contact_operator", { contact })
          : t("orders.cancel_booking.contact_operator_unknown")}
      </p>
    );
  }
  if (!state.eligible) return null;
  return (
    <button
      type="button"
      onClick={onCancelClick}
      disabled={busy}
      className="my-2 text-sm underline text-foreground/80 self-center"
    >
      {t("orders.cancel_booking.link")}
    </button>
  );
};

const OrdersPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [initialServiceSelected, setInitialServiceSelected] =
    useState<ServiceTypeSelectorOption>();
  let { selectedBooking, bookingSummary } = useSelector(
    (state: RootState) => state.booking
  );
  const [searchParams] = useSearchParams();
  const { selectedServiceType } = useSelector(
    (state: RootState) => state.booking
  );
  const gracePeriodMinutes = useSelector(
    (state: RootState) => state.tenantConfig.config.gracePeriodMinutes
  );
  const tenantConfig = useSelector(
    (state: RootState) => state.tenantConfig.config
  );
  const [filteredBookingSummary, setFilteredBookingSummary] = useState<
    BookingSummaryProps[]
  >([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  // Why the booking behind an email deep link did not show up, or null while
  // nothing is wrong. Bumping `deepLinkRetry` re-runs the fetch effect.
  const [deepLinkFailure, setDeepLinkFailure] =
    useState<OrderLoadFailure | null>(null);
  const [deepLinkRetry, setDeepLinkRetry] = useState(0);
  const [openSelectLocationDialog, setOpenSelectLocationDialog] =
    React.useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useDispatch();

  const { rentalFinished, rentalStarted, itemState } = useSelector(
    (state: RootState) => state.booking
  );
  const [trigger, { data: ordersUpdate }] = useLazyGetUserOrderQuery();
  const [triggerSubscription, { data: subscriptionsUpdate }] = useLazyGetCustomerAgreementsByIdsQuery();
  const selectedSubscription = useSelector((state: RootState) => state.subscription.selectedSubscription);
  const [cancelSubscription, { isLoading: isCancelling }] = useCancelSubscriptionMutation();

  const startRental = useStartRental(selectedBooking);
  const selectedBookingRental = selectedBooking?.items?.[0]?.rental;
  const finishSelectedRental = useFinishRental(selectedBookingRental);

  // ── Generic check-in/check-out CTA (venue/apartment only) ─────────────
  // For venue/apartment orders on tenants with the generic flow enabled, the
  // card CTA drives the usage_log flow (#1155) instead of the legacy rental
  // start. Every other service keeps its existing button untouched.
  // Gated per use case (PO, 2026-09-03): the flow is resolved for THIS
  // booking's service, so a tenant running it for apartments only does not get
  // the usage CTA on their venue cards. The venue/apartment restriction stays
  // explicit — those are the only two services the generic flow serves.
  const selectedFlow = useUseCaseFlow(selectedBooking?.service);
  const isGenericVenueApartment =
    selectedFlow !== null && isRoomFamily(selectedBooking?.service);
  // The space properties of the selected booking's use case, for the
  // event-vs-stay questions below; `null` for every other service.
  const selectedSpace = spaceOf(selectedBooking?.service);

  // #2352: a venue reservation the operator has not decided on yet. Named once
  // here because two things on this screen ask the same question — which note
  // the ticket carries, and whether it offers check-in at all.
  const venueAwaitsDecision =
    !!selectedBooking &&
    selectedSpace?.flow === "event" &&
    isVenueReviewPending(selectedBooking);

  // Check-in state of the selected generic booking. Skipped for every other
  // card so we never hit the usage_log endpoint needlessly.
  const { data: usageLog } = useGetUsageLogQuery(
    { orderId: selectedBooking?.id ?? "" },
    { skip: !isGenericVenueApartment || !selectedBooking?.id }
  );
  const usageStatus = deriveUsageStatus(usageLog);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [startUsed, setStartUsed] = React.useState<boolean>(false);
  const [pageDialogOpen, setPageDialogOpen] = React.useState<boolean>(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = React.useState<boolean>(false);
  const [selfConfirmDialogOpen, setSelfConfirmDialogOpen] =
    React.useState<boolean>(false);

  const query = new URLSearchParams(useLocation().search);
  const token = query.get("token");
  const location = useLocation();
  const prevLocationRef = useRef<string>();
  // Tracks the `?order_id=` we've already focused, so the email deep link
  // selects its booking exactly once and never overrides in-session carousel
  // navigation on a later refetch.
  const focusedOrderIdRef = useRef<string | null>(null);

  const handleRetrieveBookingDetails = () => {
    navigate(routes.retrieveBookingEmailPath);
  };

  const indexDBStore = useMemo(() => new KolulaStorageManager(), []);

  // #1874: single branch point for the order-id source. Logged in (accounts
  // enabled + profile) the ids come from GET /me/bookings; logged out they
  // come from IndexedDB exactly as before. Everything downstream (detail
  // loading, service chips, ticket cards) is shared by both modes.
  const { isAccountMode, accountOrderIds } = useAccountOrderIdSource();

  // #1495 self-cancel: the eligibility fields (coveredByOrg, paymentStatus,
  // isCanceled) live on the /me/bookings summary, not on the old-backend
  // order the cards render from. Same cache entry as the id-source hook's
  // query — this adds no request.
  const { data: accountBookings, refetch: refetchAccountBookings } = useGetMyBookingsQuery(undefined, {
    skip: !isAccountMode,
  });
  const [cancelMyBooking, { isLoading: isCancellingBooking }] =
    useCancelMyBookingMutation();
  const [cancelBookingDialogOpen, setCancelBookingDialogOpen] = useState(false);

  function sortOrders(orders: BookingSummaryProps[]): BookingSummaryProps[] {
    return [...orders].sort((a, b) => {
      const getStatus = (order: BookingSummaryProps) => {
        const item = order.items[0];
        if (item.rental?.startedAtDate && !item.rental?.finishedAtDate) {
          return 1; // Started but not finished
        } else if (!item.rental?.startedAtDate) {
          return 2; // Not started
        } else {
          return 3; // Finished or expired
        }
      };

      const statusA = getStatus(a);
      const statusB = getStatus(b);

      if (statusA !== statusB) return statusA - statusB;

      if (statusA === 1) {
        return (
          new Date(a.items[0].toDate).getTime() -
          new Date(b.items[0].toDate).getTime()
        );
      }
      if (statusA === 2) {
        // Upcoming (not started): newest-first by creation date so the
        // booking the user just made surfaces near the top of the
        // carousel instead of sitting behind older, stale upcoming ones.
        return (
          new Date(b.createdDate).getTime() -
          new Date(a.createdDate).getTime()
        );
      }
      if (statusA === 3) {
        const dateA =
          a.items[0].rental?.finishedAtDate ||
          a.items[0].rental?.expirationDate;
        const dateB =
          b.items[0].rental?.finishedAtDate ||
          b.items[0].rental?.expirationDate;

        return new Date(dateA).getTime() - new Date(dateB).getTime();
      }

      return 0;
    });
  }

  const filterAndSortBookingSummary = (selectedServiceType: string) => {
    if (selectedServiceType === ServiceTypeSelectorOption.ALL) {
      const sortedOrders = sortOrders(bookingSummary);
      setFilteredBookingSummary(sortedOrders || []);
      return;
    }

    const filteredOrders = bookingSummary.filter(
      (booking: any) => booking.service === selectedServiceType
    );
    const sortedAndFilteredOrders = sortOrders(filteredOrders);
    setFilteredBookingSummary(sortedAndFilteredOrders);
  };

  const searchOrderId = searchParams.get("order_id");

  // Hoisted out of the fetch effect on purpose: written inline there, this
  // predicate sits five function levels deep — SonarJS flags it, and it is
  // genuinely harder to read at that depth than as a named rule here.
  const isDeepLinkedOrder = (order: { id: string }) =>
    order.id === searchOrderId;

  useEffect(() => {
    dispatch(setIsPaymentVerified(null));
  });

  useEffect(() => {
    if (selectedBooking?.items?.[0]?.rental && !isEmptyObject(selectedBooking.items[0].rental)) {
      dispatch(setRentalStarted({ rental: selectedBooking.items[0].rental }));
    }
  }, [selectedBooking, dispatch]);

  useEffect(() => {
    const rental = selectedBooking?.items?.[0]?.rental;
    // Backend serializes an unfinished rental's finishedAtDate as ""; treat
    // it as not-finished (matches getBookingStatusType's Boolean check).
    const hasFinishedDate = Boolean(
      (rental as any)?.finishedAtDate || (rental as any)?.finished_at
    );
    if (hasFinishedDate) {
      dispatch(setRentalFinished({ orderId: selectedBooking!.id, updatedOrder: selectedBooking! }));
    } else if (selectedBooking) {
      dispatch(resetRentalFinished());
    }
  }, [selectedBooking, dispatch]);

  // Refetch selectedBooking when returning from order details dialog
  useEffect(() => {
    const currentPath = location.pathname;
    const wasInOrderDetails = prevLocationRef.current?.startsWith('/orders/');
    const isNowInOrdersList = currentPath === routes.orders;

    if (wasInOrderDetails && isNowInOrdersList && selectedBooking?.id) {
      const bookingId = selectedBooking.id;
      const refetchBooking = async () => {
        const result = await trigger({ orderIds: bookingId });
        // Re-select the order matching the id we asked for — never a blind
        // data[0], which breaks as soon as the response carries siblings.
        const refetchedBooking = result.data?.find(
          (order: { id: string }) => order.id === bookingId
        );
        if (refetchedBooking) {
          dispatch(setSelectedBooking(refetchedBooking));
        }
      };
      refetchBooking();
    }

    prevLocationRef.current = currentPath;
  }, [location.pathname, selectedBooking?.id, trigger, dispatch]);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoadingOrders(true);
      setDeepLinkFailure(null);

      const hasAuthentication = tenantConfig?.authentication && tenantConfig.authentication !== 'none';
      
      const fetchSearchOrder = hasAuthentication
      ? (async () => {
            // Fetch orders by token for authenticated tenants
            const result: any = await baseStore.dispatch(
              bookingsApiSlice.endpoints.getOrdersByToken.initiate({})
            );
            return result?.data || [];
          })()
        : (await searchOrderId)
        ? (async () => {
            // The email deep link carries one order id per ticket — by
            // design: a forwarded email hands over exactly that one ticket.
            // Persist every id the link names (put is an idempotent upsert)
            // and don't crash on an unknown id — previously only data[0]
            // was stored and an invalid link threw before rendering.
            // `forceRefetch`, or "Erneut versuchen" would be a button that
            // does nothing: RTK Query caches a FAILED query under the same
            // argument, so a second `initiate` with the same order id inside
            // the cache lifetime hands back the very error the customer is
            // trying to get past, without touching the network. Same reason
            // as `finishRentalFlow.ts:98`.
            const result: any = await baseStore.dispatch(
              bookingsApiSlice.endpoints.getUserOrder.initiate(
                { orderIds: searchOrderId },
                { forceRefetch: true }
              )
            );
            const fetchedOrders = result?.data || [];

            // Why the ticket did not appear, told apart into its three
            // causes rather than collapsed into one. Without this the page
            // fell through to "no bookings", so a dead link, a dead network
            // and a server error all rendered as an absence.
            setDeepLinkFailure(
              classifyOrderLoadFailure({
                error: result?.error,
                foundOrder: fetchedOrders.some(isDeepLinkedOrder),
              })
            );

            if (fetchedOrders.length > 0) {
              const indexDBStore = new KolulaStorageManager();
              await indexDBStore.insertInternalMultipleOrdersIds(
                fetchedOrders.map((order: { id: string }) => order.id)
              );
            }

            return fetchedOrders;
          })()
        : Promise.resolve([]);

      // The overlay comes down when the request SETTLES, not when it is sent.
      // It used to be cleared on the line above the await, which on a fast
      // connection is invisible and on a slow one is the whole wait: the page
      // rendered as if it were loaded, without the booking that the email deep
      // link had just asked for. A customer at the KGSt-Forum read that as a
      // broken link and gave up — the delay looked like an absence.
      //
      // `.finally` keeps the resolved value and rethrows a rejection, so the
      // behaviour on failure is unchanged apart from the overlay coming down.
      // Same idiom as the use-order flows (`useOrder/*/UseOrderFlow.tsx`).
      const searchedOrders = await fetchSearchOrder.finally(() => {
        setLoadingOrders(false);
      });

      // Deep-linked orders (incl. siblings) can overlap with the ones loaded
      // from IndexedDB — keep one entry per order id.
      const mergedOrders = [...searchedOrders, ...(ordersUpdate || [])];
      const combinedData = mergedOrders.filter(
        (order, index) =>
          mergedOrders.findIndex((other) => other.id === order.id) === index
      );

      const orderRentalStatus = combinedData.reduce(
        (acc: Record<string, boolean>, item) => {
          acc[item.id] =
            item?.items?.length > 0 && !isEmptyObject(item.items[0]?.rental);
          return acc;
        },
        {}
      );

      dispatch(setIsFirstTimeUsingSelectedLocker(orderRentalStatus));
      dispatch(setBookingSummary(combinedData));

      // Email deep link (`?order_id=X` — the viewOrdersAction link the
      // pre-check-in / reminder mails carry) must land the customer on THAT
      // ticket, not the carousel's default (first) slide: the fetch above adds
      // the order to the list but doesn't select it, and BookingSummary derives
      // its focused slide from `selectedBooking`. Select the matched order here.
      // Guarded by id so it fires once per deep link and doesn't fight the
      // user's own carousel navigation on a later refetch.
      if (searchOrderId && focusedOrderIdRef.current !== searchOrderId) {
        const matched = combinedData.find(
          (order) => order.id === searchOrderId
        );
        if (matched) {
          dispatch(setSelectedBooking(matched));
          focusedOrderIdRef.current = searchOrderId;
        }
      }

      // D2: drop per-device location-verification records for locations that
      // no longer have an open (unfinished) booking, so a stale verification
      // can't leak into a later booking on the same device. Multi-board-safe:
      // a location stays verified as long as any of its bookings is still
      // open. Only prune once real data is in — an empty first render must
      // not wipe records of a user who does have active bookings.
      if (combinedData.length > 0) {
        const openLocationIds = combinedData
          .filter((order) => {
            const items = order?.items;
            if (!items || items.length === 0) return true;
            return items.some(
              (item: Item) => !item?.rental?.finishedAtDate
            );
          })
          .map((order) => order?.locationId)
          .filter(Boolean);
        pruneLocationVerifications(openLocationIds);
      }
    };

    fetchOrders();
  }, [ordersUpdate, searchOrderId, tenantConfig, deepLinkRetry]);

  useEffect(() => {
    async function getLocalSubscriptions() {
      const subscriptionIds = await indexDBStore.getActiveSubscriptionIds();
      const combineIds = [
        ...(subscriptionIds || []),
      ];

      const sanitizedSubscriptionIds = combineIds.map(
        (item: { id: string }) => item.id
      );

      if (combineIds) {
        triggerSubscription(sanitizedSubscriptionIds.toString());
      }
    }

    async function getLocalOrders() {
      let retrievedOrdersDetails: Array<Reservation> | null = null;
      try {
        if (token) {
          const result = await baseStore.dispatch(
            locationsApiSlice.endpoints.getBookingDetailsByToken.initiate({
              token,
            })
          );
          if (result.error) {
            throw new Error(
              (result.error as any).data?.message ||
                getI18n().t(
                  `orders.retrieve_bookings_via_email.retrieval_error`
                )
            );
          }
          retrievedOrdersDetails = result.data;
          dispatch(
            enqueueNotification({
              severity: "success",
              message: getI18n().t(
                `orders.retrieve_bookings_via_email.success`
              ),
            })
          );
        }
      } catch (error: any) {
        dispatch(
          enqueueNotification({
            severity: "error",
            message:
              error.message ||
              getI18n().t(`orders.retrieve_bookings_via_email.error`),
          })
        );
      }

      // Retrieving via the emailed token ("retrieve existing booking") is the
      // booking person's recovery path — it returns ALL their orders, incl.
      // every board of a multi-board booking. Pin the still-current tickets
      // to this device so they survive reopening the app without the link:
      // end date not passed yet, or usage started but not finished (covers
      // overtime). Old completed bookings are shown this session only.
      if (retrievedOrdersDetails?.length) {
        const currentOrderIds = retrievedOrdersDetails
          .filter((order) => {
            const item = order.items?.[0];
            if (!item) return false;
            const started = Boolean(item.rental?.startedAtDate);
            const finished = Boolean(item.rental?.finishedAtDate);
            const endInFuture = new Date(item.toDate) > new Date();
            return endInFuture || (started && !finished);
          })
          .map((order) => order.id);
        if (currentOrderIds.length) {
          await indexDBStore.insertInternalMultipleOrdersIds(currentOrderIds);
        }
      }

      // #1874 PO decision: logged in, this tab shows ONLY account-linked
      // bookings (GET /me/bookings) — guest tickets created on this device
      // before login stay hidden until logout. The account ids are order
      // UUIDs, the same ids getUserOrder takes, in the same { id } shape as
      // getActiveOrderIds returns.
      const orderIds = isAccountMode
        ? accountOrderIds
        : await indexDBStore.getActiveOrderIds();

      const combineIds = [
        ...(orderIds || []),
        ...(retrievedOrdersDetails
          ? transformReservations(retrievedOrdersDetails)
          : []),
      ];
      const sanitizedOrderIds = combineIds.map(
        (item: { id: string }) => item.id
      );

      if (combineIds) {
        trigger({ orderIds: sanitizedOrderIds.toString() });
      }
    }

    getLocalOrders();
    getLocalSubscriptions();
  }, [indexDBStore, tenantConfig, isAccountMode, accountOrderIds]);

  const getInitialServiceState = (bookingSummary: BookingSummaryProps[]) => {
    const serviceMapping: Record<ServiceTypeSelectorOption, string> = {
      [ServiceTypeSelectorOption.RENTAL]: "RENTAL",
      [ServiceTypeSelectorOption.APARTMENT]: "APARTMENT",
      [ServiceTypeSelectorOption.KEYS]: "KEYS",
      [ServiceTypeSelectorOption.VENUE]: "VENUE",
      [ServiceTypeSelectorOption.HALL]: "HALL",
      [ServiceTypeSelectorOption.STORAGE]: "STORAGE",
      [ServiceTypeSelectorOption.P2P]: "P2P",
      [ServiceTypeSelectorOption.ALL]: "ALL",
      [ServiceTypeSelectorOption.PARCEL]: "PARCEL",
      [ServiceTypeSelectorOption.SUP]: "SUP",
      [ServiceTypeSelectorOption.VOUCHER]: "VOUCHER",
      [ServiceTypeSelectorOption.MORE_TO_COME]: "MORE_TO_COME",
      [ServiceTypeSelectorOption.SUBSCRIPTIONS]: "SUBSCRIPTIONS",
    };
    const lastCreated = bookingSummary.reduce((latest, current) =>
      new Date(current.createdDate) > new Date(latest.createdDate)
        ? current
        : latest
    );

    return serviceMapping[lastCreated.service as ServiceTypeSelectorOption];
  };

  const handleClickOpen = () => {
    switch (selectedServiceType) {
      case ServiceTypeSelectorOption.RENTAL:
        navigate(routes.bookLocationEquipmentRentalPath);
        break;
      case ServiceTypeSelectorOption.APARTMENT:
        navigate(routes.roomRentalPath);
        break;
      case ServiceTypeSelectorOption.STORAGE:
        navigate(routes.temStorageNewBooking);
        break;
      case ServiceTypeSelectorOption.P2P:
        navigate(routes.bookLocationP2PPath);
        break;
      case ServiceTypeSelectorOption.PARCEL:
        navigate(routes.bookParcelDeliveryPath);
        break;
      case ServiceTypeSelectorOption.SUP:
        navigate(routes.bookLocationSUPPath);
        break;
      case ServiceTypeSelectorOption.KEYS:
        navigate(routes.bookLocationKeysPath);
        break;
      default:
        navigate(routes.home);
    }
  };

  const handleUsingOrder = () => {
    if (!rentalStarted) {
      return startUsingOrder();
    } else {
      if (selectedBooking) {
        return navigate(routes.makeOrderDetailsUrl(selectedBooking.id));
      }
    }
  };

  const isBookingStartDateInFuture = (order: BookingSummaryProps): boolean => {
    const now = moment();
    const startDate = moment(order.items[0].fromDate);
    return now.isBefore(startDate);
  };

  const handleClose = () => {
    dispatch(setHasSelectedLocation(false));
    dispatch(setSelectedLocation(null));
    setOpenSelectLocationDialog(false);
  };

  const handleCancelSubscription = () => {
    setPageDialogOpen(true);
  };

  const isSubscriptionCancelDisabled = (): boolean => {
    if (!selectedSubscription) return true;
    if (isCancelling) return true;
    // Active and not yet scheduled to end — the one rule, shared with the
    // device-independent manage page.
    return !getSubscriptionActions(selectedSubscription).canCancel;
  };

  // The withdrawal button (§ 356a BGB) shows exactly while the backend accepts
  // a withdrawal — `withdrawableUntil` is its one rule, not re-derived here.
  const isSubscriptionWithdrawable = (): boolean => {
    if (!selectedSubscription) return false;
    return getSubscriptionActions(selectedSubscription).canWithdraw;
  };

  const refreshSubscriptions = async () => {
    const subscriptionIds = await indexDBStore.getActiveSubscriptionIds();
    const combineIds = [...(subscriptionIds || [])];
    const sanitizedSubscriptionIds = combineIds.map((item: { id: string }) => item.id);
    if (combineIds.length > 0) {
      triggerSubscription(sanitizedSubscriptionIds.toString());
    }
  };

  const handleConfirmWithdrawSubscription = async () => {
    if (!selectedSubscription) {
      setWithdrawDialogOpen(false);
      return;
    }
    try {
      await cancelSubscription({ id: selectedSubscription.id, withdraw: true }).unwrap();
      dispatch(
        enqueueNotification({
          severity: "success",
          message: t("subscriptions.withdraw_dialog.success_message"),
        })
      );
      await refreshSubscriptions();
    } catch (error: any) {
      dispatch(
        enqueueNotification({
          severity: "error",
          message: withdrawErrorMessage(error, t),
        })
      );
    } finally {
      setWithdrawDialogOpen(false);
    }
  };

  const handleConfirmCancelSubscription = async () => {
    if (!selectedSubscription) {
      setPageDialogOpen(false);
      return;
    }

    try {
      await cancelSubscription({ 
        id: selectedSubscription.id, 
        cancelAtPeriodEnd: true 
      }).unwrap();
      dispatch(
        enqueueNotification({
          severity: "success",
          message: t("subscriptions.cancel_dialog.success_message"),
        })
      );
      
      // Refresh subscription data
      await refreshSubscriptions();
      
      setPageDialogOpen(false);
    } catch (error: any) {
      dispatch(
        enqueueNotification({
          severity: "error",
          message: error?.data?.message || t("subscriptions.cancel_dialog.error_message"),
        })
      );
      setPageDialogOpen(false);
    }
  };
  const [open, setOpen] = useState(false);
  const handleToggle = () => setOpen((prev) => !prev);
  
  // Generic remote unlock detection: Button should only be shown for ALL flows
  // if at least one item has supports_remote_unlock (boolean true or string != 'none').
  const supportsRemoteUnlock: boolean = Boolean(
    selectedBooking?.items?.some((it) => it?.supports_remote_unlock === true)
  );
  // Visibility of the start/open button is checked during render (supportsRemoteUnlock)

  const renderRightButton = () => {
    // Check if this is a key return booking
    if (selectedBooking?.service === ServiceTypeSelectorOption.KEYS) {
      const firstItem = selectedBooking.items[0];
      const itemName =
        firstItem.group?.name ||
        firstItem.group?.langNames?.[i18n.language]?.title;

      if (itemName === t("book_location.keys.summary.key_return")) {
        dispatch(setIsSelectedBookingHostKeyRetrieval(true));
        return t("book_location.keys.key_retrieval.retrieve_key");
      } else {
        dispatch(setIsSelectedBookingHostKeyRetrieval(false));
      }
    } else {
      dispatch(setIsSelectedBookingHostKeyRetrieval(false));
    }

    const actionKey =
      selectedBooking?.service === ServiceTypeSelectorOption.PARCEL
        ? "book_location.orders_page.retrieve_item"
        : !rentalStarted && !rentalFinished
        ? "book_location.orders_page.start_using"
        : isRoomFamily(selectedBooking?.service)
        ? "use_order.room_rental.unlock_door"
        : selectedBooking?.service === ServiceTypeSelectorOption.SUP ||
          selectedBooking?.service === ServiceTypeSelectorOption.RENTAL
        ? "use_order.open_door"
        : "book_location.orders_page.open_order";

    return t(actionKey);
  };

  const disableButton = () => {
    if (
      rentalFinished ||
      ["item_retrieved_confirmed"].includes(itemState ?? "")
    ) {
      return true;
    }
    if (selectedBooking && new Date() > new Date(selectedBooking.items[0].toDate)) {
      const pastEndRental = selectedBooking.items[0].rental;
      // A booking with an overtime allowance may still be opened ONCE past its
      // end — to return the asset, or to empty the compartment — but only while
      // the rental is genuinely active. Old, finished or never-started past-end
      // bookings must keep the CTA disabled; otherwise tapping it fires
      // startRental on a stale booking (see #1035 item 3).
      //
      // This is the UI half of the allowance the door enforces server-side
      // (`overtimeAccess.ts`). It listed `rental || sup` by hand until
      // 2026-09-14, so a storage customer whose booking had ended found the
      // button greyed out — no opening at all, where the rule grants one.
      if (
        hasOvertimeAllowance(selectedBooking.service)
        && pastEndRental?.isActive
        && !pastEndRental?.openedDuringOvertime
      ) {
        return false;
      }
      return true;
    }
    if (
      itemState === "item_placement_confirmed" &&
      selectedBooking &&
      (selectedBooking.service === "p2p")
    ) {
      return true;
    }

    // Disable B postfix keys bookings if order hasn't started yet
    if (
      selectedBooking &&
      selectedBooking.service === ServiceTypeSelectorOption.KEYS &&
      selectedBooking.bookingNumber.endsWith("B") &&
      !selectedBooking.items[0].rental?.isActive
    ) {
      return true;
    }

    if (selectedBooking) {
      if (selectedBooking.service === "parcel") {
        if (Object.keys(selectedBooking.items[0].rental).length === 0) {
          return true;
        } else if (
          selectedBooking.items[0].rental.state === "item_retrieved_confirmed"
        ) {
          return true;
        }
      }
      return !checkIfOrderCanBeStarted(selectedBooking, gracePeriodMinutes);
    }
    return false;
  };

  // Generic venue/apartment CTA: label reflects the usage_log state. While
  // checked in, the CTA opens the persistent active-usage screen (which hosts
  // help + the check-out entry point) — it is deliberately not labeled as a
  // finish action, because tapping it never ends the usage by itself.
  // Enablement follows the booking window, and check-in additionally requires
  // a fully-paid ("Booked") order. Tapping opens the flow at /orders/:orderId,
  // which restores the right step from the backend status.
  const genericCtaLabel = (): string =>
    usageStatus === "active"
      ? t("book_location.orders_page.show_active_booking")
      : t("book_location.orders_page.check_in");

  const disableGenericCta = (): boolean => {
    if (!selectedBooking) return true;
    // Checked in: always reachable — an active usage stays live through
    // overtime (state model: the customer keeps the ability to finish), so the
    // active-booking screen (help + check-out) must never lock behind the
    // booking window.
    if (usageStatus === "active") return false;
    // Check-in: booking window + nothing outstanding.
    //
    // #2040: this used to read `wasPaid`, the STICKY flag, while the comment
    // already said "fully paid". A booking paid at creation and then edited
    // into a pricier slot kept `was_paid === true`, so the customer could start
    // the rental owing the difference. `hasOpenBalance` reads the current
    // balance and is the same rule the badge below uses.
    //
    // #1704: it also reads the contract axis, so a POSTPAY booking — unpaid on
    // purpose, settled by invoice after use — is not locked out of its own
    // check-in. The whole booking is passed, so the field flows in without a
    // second predicate here.
    const withinBookingWindow = checkIfOrderCanBeStarted(
      selectedBooking,
      gracePeriodMinutes
    );
    return !(!hasOpenBalance(selectedBooking) && withinBookingWindow);
  };

  const handleGenericUse = () => {
    if (selectedBooking) {
      navigate(routes.makeOrderDetailsUrl(selectedBooking.id));
    }
  };

  // The one CTA slot under the ticket. Extracted from the JSX because four
  // conditions decide what belongs there and reading them as a ternary chain
  // was already hard before #2352 added a fifth.
  const renderTicketCta = (): JSX.Element => {
    // #2352: no check-in on a reservation nobody has approved. The customer
    // has asked for the room and is waiting for an answer; offering to check
    // in — enabled or greyed out — says the booking exists. Checked first, so
    // it covers BOTH buttons below: a venue tenant without the generic flow
    // renders the legacy start button from this same slot.
    if (venueAwaitsDecision) return <div className="mt-10" />;

    // Venue/apartment generic flow: check-in / finish CTA. Hidden once the
    // booking is finished.
    if (isGenericVenueApartment) {
      if (usageStatus === "finished") return <div className="mt-10" />;
      return (
        <CustomButton
          isButtonDark={true}
          onClick={handleGenericUse}
          disabled={disableGenericCta()}
          children={genericCtaLabel()}
        />
      );
    }

    if (supportsRemoteUnlock) {
      return (
        <CustomButton
          isButtonDark={true}
          onClick={handleUsingOrder}
          disabled={disableButton()}
          children={renderRightButton()}
        />
      );
    }

    // No remote unlock: hide the button completely (keep spacing).
    return <div className="mt-10" />;
  };

  const startUsingOrder = async () => {
    if (!selectedBooking) return;

    const service = selectedBooking.service as ServiceTypeSelectorOption;
    const supportsStart = supportsStartRental(service);

    // Flows without actual rental start
    if (!supportsStart) {
      return navigate(routes.makeOrderDetailsUrl(selectedBooking.id || ""));
    }

    // Parcel flow
    if (service === ServiceTypeSelectorOption.PARCEL) {
      return navigate(routes.makeOrderDetailsUrl(selectedBooking.id || ""));
    }

    // Standard flow: Start rental, then navigate to details page in effect
    setLoading(true);
    await startRental();
    setStartUsed(true);
  };

  useEffect(() => {
    if (rentalStarted && selectedBooking && startUsed) {
      const url = routes.makeOrderDetailsUrl(selectedBooking.id || "");
      setLoading(false);
      navigate(url);
      setStartUsed(false);
    } else if (selectedBooking && startUsed && rentalStarted === false) {
      setStartUsed(false);
      setLoading(false);
    }
  }, [rentalStarted, startUsed]);

  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    if (!initialServiceSelected && bookingSummary.length > 0) {
      const initialService = getInitialServiceState(
        bookingSummary
      ) as keyof typeof ServiceTypeSelectorOption;
      setInitialServiceSelected(ServiceTypeSelectorOption[initialService]);
    }

    filterAndSortBookingSummary(selectedServiceType);
  }, [bookingSummary, selectedServiceType]);



  const differentiateSameBookings = useMemo<BookingGroup[]>(() => {
    const alphabets = [
      "A",
      "B",
      "C",
      "D",
      "E",
      "F",
      "G",
      "H",
      "I",
      "J",
      "K",
      "L",
      "M",
      "N",
      "O",
      "P",
      "Q",
      "R",
      "S",
      "T",
      "U",
      "V",
      "W",
      "X",
      "Y",
      "Z",
    ];

    // Group by bookingNumber
    const grouped = filteredBookingSummary.reduce<Record<string, any[]>>(
      (map, booking) => {
        (map[booking.bookingNumber] ||= []).push(booking);
        return map;
      },
      {}
    );

    // Assign letters only for groups with more than 1 booking
    const bookingsWithLetters = Object.values(grouped).flatMap((group) => {
      if (group.length <= 1) {
        return group;
      }

      const groupService = group?.[0]?.service;

      if (groupService === ServiceTypeSelectorOption.KEYS) {
        return [...group]
          .sort((a, b) =>
            a?.items?.[0]?.toDate.localeCompare(b?.items?.[0]?.toDate)
          )
          .map((booking, index) => {
            const itemName =
              index === 0
                ? t("book_location.keys.summary.key_pickup")
                : t("book_location.keys.summary.key_return");

            const bookingItems = booking.items.map((item: Item) => ({
              ...item,
              group: {
                ...item.group,
                name: itemName,
                langNames: {
                  de: {
                    ...item.group?.langNames?.de,
                    title: itemName,
                  },
                  en: {
                    ...item.group?.langNames?.en,
                    title: itemName,
                  },
                },
              },
            }));
            return {
              ...booking,
              // Preserve the original (suffix-free) booking number so the
              // D4 carousel grouping below can still find sibling tickets
              // even after we mutate the displayed `bookingNumber` with
              // an A/B/… letter.
              originalBookingNumber: booking.bookingNumber,
              bookingNumber: `${booking.bookingNumber}${
                alphabets[index] || ""
              }`,
              items: bookingItems,
            };
          })
          .sort((a, b) => a.id.localeCompare(b.id));
      }

      return [...group]
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((booking, index) => ({
          ...booking,
          // See KEYS branch above for the rationale.
          originalBookingNumber: booking.bookingNumber,
          bookingNumber: `${booking.bookingNumber}${alphabets[index] || ""}`,
        }));
    });
    const differentiatedAndSortedBookings = filteredBookingSummary.map(
      (orig) => bookingsWithLetters.find((b) => b.id === orig.id) || orig
    );

    // D4: regroup for the carousel. SUP + Product Rental multi-board
    // bookings get all their boards stacked inside one slide so none hide
    // behind a swipe (F6). KEYS pickup + return stay as separate slides
    // per PO scope guard (two sequential steps, not parallel sub-tickets).
    // Single-board bookings produce a slide of one card — visually
    // unchanged.
    const groups: BookingGroup[] = [];
    const groupedByNumber = new Map<string, BookingGroup>();
    for (const booking of differentiatedAndSortedBookings) {
      const service = (booking.service || "").toLowerCase();
      const isStackable =
        service === ServiceTypeSelectorOption.SUP ||
        service === ServiceTypeSelectorOption.RENTAL;
      // KEYS / parcel / p2p / storage / room / apartment all stay 1:1
      // even when sharing a bookingNumber. Use the booking's own id as
      // the group key for them so each becomes its own slide.
      //
      // For stackable services, key off the *original* bookingNumber —
      // not the displayed one, which the letter-suffix pass above
      // mutated to e.g. "S-12345A" / "S-12345B". `originalBookingNumber`
      // is set only on bookings that came out of a multi-board group
      // upstream; single-board SUP / Product Rental bookings don't have
      // it and MUST fall through to `booking.id` (not `booking.bookingNumber`)
      // to avoid an accidental collision with an unrelated multi-board
      // group whose `originalBookingNumber` happens to equal this
      // single booking's display number.
      const originalBookingNumber = (
        booking as { originalBookingNumber?: string }
      ).originalBookingNumber;
      const groupKey =
        isStackable && originalBookingNumber
          ? originalBookingNumber
          : booking.id;
      const existing = groupedByNumber.get(groupKey);
      if (existing) {
        existing.boards.push(booking);
      } else {
        const group: BookingGroup = {
          id: groupKey,
          service: booking.service,
          boards: [booking],
        };
        groupedByNumber.set(groupKey, group);
        groups.push(group);
      }
    }
    // Sort each group's boards by their displayed (letter-suffixed)
    // bookingNumber so the accordion in BookingSummary renders in
    // strict A → B → C order regardless of how the boards happened
    // to fall out of `filteredBookingSummary`.
    for (const g of groups) {
      g.boards.sort((a, b) =>
        (a.bookingNumber || "").localeCompare(b.bookingNumber || "")
      );
    }
    return groups;
  }, [filteredBookingSummary]);

  // D6/#3.5: in-app self-confirm — visible on overtime tickets for every use
  // case with an overtime allowance: SUP, product rental and, since #2652,
  // storage (lead dev 2026-11-03, "like SUP"). A storage customer who had used
  // the one overtime opening found "open" and "end usage" locked together and
  // could not end the booking (#2649); this keeps ending reachable on the
  // ticket. Keys, parcel and p2p have different finish semantics.
  const isSelfConfirmEligible = (() => {
    if (!selectedBooking) return false;
    const service = selectedBooking.service;
    if (!service || !hasOvertimeAllowance(service)) return false;
    const rental = selectedBooking.items?.[0]?.rental;
    if (!rental || isEmptyObject(rental as object)) return false;
    const status = getBookingStatusType(
      rental,
      service,
      selectedBooking.items[0],
      selectedBooking.wasPaid,
      selectedBooking.isPaid,
      selectedBooking.contractConcludedAt,
      selectedBooking.paymentStatus,
    );
    return status === "overtime";
  })();

  // #1495 / M10 — self-cancel while no money moved, up to the start, under
  // the category's cancellation rule. The SERVER decides (the `cancellation`
  // verdict on /me/bookings, enforced by the cancel route); this renders it.
  // Mapping lives at module level (deriveSelfCancelState) — the component
  // function sits at Sonar's cognitive-complexity ceiling.
  const selfCancelState = deriveSelfCancelState(
    isAccountMode,
    selectedBooking,
    accountBookings
  );

  const handleConfirmCancelBooking = async () => {
    if (!selectedBooking) {
      setCancelBookingDialogOpen(false);
      return;
    }
    try {
      const terms = selfCancelState.terms;
      await cancelMyBooking({
        orderId: selectedBooking.id,
        // W1-6: the fee the dialog showed — the server charges exactly this.
        ...(terms?.kind === "fee" ? { expectedFee: terms.amount } : {}),
      }).unwrap();
      dispatch(
        enqueueNotification({
          severity: "success",
          message: t("orders.cancel_booking.success_message"),
        })
      );
      // The cards render from the old-backend order — refetch so the
      // cancelled state is visible without a reload (the /me/bookings list
      // refreshes itself via the mutation's tag).
      const ids = accountOrderIds.map((entry) => entry.id);
      if (ids.length) {
        trigger({ orderIds: ids.toString() });
      }
    } catch (error: any) {
      dispatch(
        enqueueNotification({
          severity: "error",
          message: cancelErrorMessage(t, error),
        })
      );
      // The list's verdict was stale (a 422 or 409): reload it, so the tab
      // shows why instead of offering the cancel again.
      if (isAccountMode) refetchAccountBookings();
    }
    setCancelBookingDialogOpen(false);
  };

  const handleSelfConfirmFinish = async () => {
    if (!selectedBookingRental) return;
    setLoading(true);
    try {
      await finishSelectedRental(undefined, "self-confirm-in-app");
    } finally {
      setLoading(false);
    }
  };

  if (loadingOrders) {
    return <LoaderOverlay open={loadingOrders} />;
  }

  return (
    <Box sx={styles.outerBox}>
      {/* Dev-only: swiping the carousel doesn't change the URL, so the
          /orders/:orderId deep-link test (D5) needs a way to reach a
          concrete order URL. This pill navigates to the currently
          focused booking's detail URL. Stripped from production. */}
      {process.env.NODE_ENV === "development" && selectedBooking?.id && (
        <button
          type="button"
          onClick={() =>
            navigate(routes.makeOrderDetailsUrl(selectedBooking!.id))
          }
          className="fixed bottom-2 right-2 z-50 rounded-md border border-dashed border-gray-400 bg-white/80 px-2 py-1 text-[10px] uppercase tracking-wide text-gray-600 shadow-sm hover:bg-white"
        >
          DEV: open /orders/{selectedBooking.id.slice(0, 8)}…
        </button>
      )}
      <Box sx={styles.innerBox}>
        <Header>
          <TenantLogo />
        </Header>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <BookLocationHeader
            text={t(
              isAccountMode
                ? "account.bookings.title"
                : "orders.ticket_overview.header"
            )}
          />
        </Box>
        <Box>
          <ServiceTypeSelector
            initialSelected={
              initialServiceSelected ?? ServiceTypeSelectorOption.RENTAL
            }
            bookingSummary={bookingSummary || []}
            subscriptionsCount={Array.isArray(subscriptionsUpdate) ? subscriptionsUpdate.length : 0}
          />
        </Box>
        {/* A failed deep link says what went wrong and, where it can help,
            offers a retry. It sits above the list rather than replacing it:
            the customer may well have other tickets cached, and those stay
            usable while this one is missing. Retry is withheld on
            `not_found` — the request arrived, the answer was "no such
            booking", and pressing again cannot produce one. */}
        {deepLinkFailure && (
          <div
            role={"alert"}
            className={
              "mx-5 mt-4 grid justify-items-center gap-3 rounded-lg " +
              "border border-appRed p-4 text-center"
            }
          >
            <p className={"text-sm text-foreground"}>
              {t(failureMessageKey(deepLinkFailure))}
            </p>
            {failureIsRetryable(deepLinkFailure) && (
              <Button
                variant={"outline"}
                onClick={() => setDeepLinkRetry((attempt) => attempt + 1)}
              >
                {t("orders.deep_link_error.retry")}
              </Button>
            )}
          </div>
        )}
        {selectedServiceType === SUBSCRIPTIONS ? (
          Array.isArray(subscriptionsUpdate) && subscriptionsUpdate.length > 0 ? (
            <>
              <SubscriptionSummary subscriptions={subscriptionsUpdate} />
            </>
          ) : (
            <Typography
              variant="h5"
              sx={{
                marginTop: "52px",
                display: "grid",
                justifyItems: "center",
                marginBottom: "10px",
              }}
            >
              {t("orders.ticket_overview.no_booking")}
            </Typography>
          )
        ) : filteredBookingSummary?.length > 0 ? (
          <>
            <BookingSummary
              userReservation={differentiateSameBookings}
              serviceType={selectedServiceType}
            />
          </>
        ) : (
          <Typography
            variant="h5"
            sx={{
              marginTop: "52px",
              display: "grid",
              justifyItems: "center",
              marginBottom: "10px",
            }}
          >
            {t("orders.ticket_overview.no_booking")}
          </Typography>
        )}
      </Box>
      {filteredBookingSummary?.length > 0 && (
        <div className="flex flex-1 justify-center flex-col w-full px-5">
          {selectedBooking?.service ===
          ServiceTypeSelectorOption.PARCEL ? null : selectedBooking?.service ===
              ServiceTypeSelectorOption.KEYS &&
            isEmptyObject(selectedBooking.items[0]?.rental) ? (
            <BookingSummaryNote serviceType={selectedServiceType} />
          ) : selectedSpace?.flow === "stay" ||
            selectedBooking?.service === ServiceTypeSelectorOption.SUP ||
            // Venue: the same predicate the note component and the CTA above
            // read, rather than a third copy of the rule. What used to sit here
            // read the money only, which a concluded postpay booking passes —
            // unpaid on purpose — so the "pending review" note reached a
            // booking the customer had just made binding (#2352).
            // Keeping the gate here rather than leaning on the component's
            // null return matters: a decided venue booking must fall through
            // to the branch below, which drops it, instead of reaching the
            // component with a non-venue tab and picking up that tab's note.
            venueAwaitsDecision ? (
            <BookingSummaryNote serviceType={selectedServiceType} />
          ) : (
            // A venue booking reaches here only once its reservation is
            // decided, and this guard is what drops it: the note below is for
            // a booking whose start is still ahead, which a concluded venue
            // booking also is.
            selectedBooking &&
            selectedSpace?.flow !== "event" &&
            isBookingStartDateInFuture(selectedBooking) &&
            getBookingStatusType(
              selectedBooking.items[0]?.rental,
              selectedServiceType,
              selectedBooking.items[0],
              selectedBooking.wasPaid,
              selectedBooking.isPaid,
              selectedBooking.contractConcludedAt,
              selectedBooking.paymentStatus,
            ) === "booked" && (
              <BookingSummaryNote serviceType={selectedServiceType} />
            )
          )}
          {renderTicketCta()}
          {isSelfConfirmEligible && (
            <button
              type="button"
              onClick={() => setSelfConfirmDialogOpen(true)}
              className="my-4 text-sm underline text-foreground/80 self-center"
            >
              {t("orders.self_confirm.link")}
            </button>
          )}
          <SelfCancelSection
            state={selfCancelState}
            busy={isCancellingBooking}
            onCancelClick={() => setCancelBookingDialogOpen(true)}
            contact={[tenantConfig?.contact?.email, tenantConfig?.contact?.phoneFormatted || tenantConfig?.contact?.phone]
              .filter((value): value is string => typeof value === "string" && value.trim() !== "")
              .join(" · ")}
          />
        </div>
      )}
      {selectedSubscription && selectedServiceType === SUBSCRIPTIONS && (
        <div className="flex flex-1 justify-center flex-col w-full px-5">
          <CustomButton
            isButtonDark={true}
            onClick={handleCancelSubscription}
            disabled={isSubscriptionCancelDisabled()}
            children={t("subscriptions.cancel_button")}
          />
          {isSubscriptionWithdrawable() && (
            // Kept apart from the cancel button (§ 356a(1) BGB: the two must
            // not be confused): a plain text button below it.
            <button
              type="button"
              onClick={() => setWithdrawDialogOpen(true)}
              disabled={isCancelling}
              className="mt-4 text-sm underline text-foreground/80 self-center"
            >
              {t("subscriptions.withdraw_button")}
            </button>
          )}
        </div>
      )}
      <div
        className={`w-full px-5 max-w-[440px] ${
          (filteredBookingSummary?.length > 0) || (selectedServiceType === SUBSCRIPTIONS && Array.isArray(subscriptionsUpdate) && subscriptionsUpdate.length > 0)
            ? "relative mb-20 "
            : "fixed  bottom-20 "
        }`}
      >
        <button
          ref={buttonRef}
          onClick={handleToggle}
          className="w-full font-semibold text-foreground/90 leading-[56px] text-[20px] bg-white border border-primary px-4 rounded-xl hover:bg-gray-100 flex items-center justify-center"
        >
          {t("orders.button.add")}
          <MdKeyboardArrowDown
            className={`ml-2 transform transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>

        {open && (
          <div
            ref={menuRef}
            className="absolute left-5 right-5 bottom-full mt-2 py-3 bg-background border border-foreground/10 rounded-xl shadow-lg z-50"
          >
            <div className="hover:bg-foreground/10">
              <button
                onClick={handleClickOpen}
                className="w-full text-left px-4 py-1"
              >
                {t("orders.button.new")}
              </button>
            </div>
            {!(tenantConfig?.authentication && tenantConfig.authentication !== 'none') && (
              <div className="hover:bg-foreground/10">
                <button
                  onClick={handleRetrieveBookingDetails}
                  className="w-full text-left px-4 py-1"
                >
                  {t("orders.button.previous")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <GeneralDialog
        open={pageDialogOpen}
        onClose={() => setPageDialogOpen(false)}
        onContinue={handleConfirmCancelSubscription}
        title={t("subscriptions.cancel_dialog.title")}
        confirmButtonText={t("subscriptions.cancel_dialog.confirm_button")}
        content={selectedSubscription ? 
          t("subscriptions.cancel_dialog.content", {
            subscriptionName: `${t("subscriptions.ticket_overview.locker")} ${selectedSubscription?.doormappings?.[0]?.door ?? t("common.missing")}`,
            address: `${selectedSubscription?.commercialModel?.location?.address?.street}, ${selectedSubscription?.commercialModel?.location?.address?.postalCode} ${selectedSubscription?.commercialModel?.location?.address?.city}`,
            price: selectedSubscription?.commercialModel?.price,
            recurrence: t(`subscriptions.recurrence.${selectedSubscription?.commercialModel?.recurrence}`),
            email: selectedSubscription?.contactdata?.email ? selectedSubscription.contactdata.email.replace(/(.{1})[^@]*(.{1}@.*)/, '$1****$2') : '',
            // On the subscription location's clock (#3219).
            endDate: selectedSubscription?.expiryDate
              ? atStation(
                  selectedSubscription.expiryDate,
                  zoneOfLocation(selectedSubscription.commercialModel?.location)
                ).format('L')
              : t("common.missing")
          }) : 
          t("use_order.closing_dialog.content")
        }
      />
      <GeneralDialog
        open={withdrawDialogOpen}
        onClose={() => setWithdrawDialogOpen(false)}
        onContinue={handleConfirmWithdrawSubscription}
        title={t("subscriptions.withdraw_dialog.title")}
        confirmButtonText={t("subscriptions.withdraw_dialog.confirm_button")}
        content={t("subscriptions.withdraw_dialog.content", {
          subscriptionName: `${t("subscriptions.ticket_overview.locker")} ${selectedSubscription?.doormappings?.[0]?.door ?? t("common.missing")}`,
        })}
      />
      
      <GeneralDialog
        open={cancelBookingDialogOpen}
        onClose={() => setCancelBookingDialogOpen(false)}
        onContinue={handleConfirmCancelBooking}
        title={t("orders.cancel_booking.dialog_title")}
        confirmButtonText={t("orders.cancel_booking.confirm_button")}
        content={cancelDialogContent(
          t,
          selfCancelState,
          zoneOfLocation({ timeZone: selectedBooking?.locationTimeZone }),
          i18n.language
        )}
      />
      <SelectLocationDialog
        handleClickOpen={handleClickOpen}
        handleClose={handleClose}
        openSelectLocationDialog={openSelectLocationDialog}
      />
      <StopUsingDialog
        open={selfConfirmDialogOpen}
        onClose={() => setSelfConfirmDialogOpen(false)}
        onFinishRental={handleSelfConfirmFinish}
      />
      <LoaderOverlay open={loading || isCancelling} />
    </Box>
  );
};

export default OrdersPage;
